import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle,
  Star,
  Clock,
  ShieldCheck,
  Sparkles,
  Flame,
  Droplet,
  Sofa,
  TreePine,
  PaintRoller,
  Award,
  CalendarCheck,
  Leaf,
  Users,
  HeartHandshake,
  Phone,
  ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Seo, breadcrumbSchema, faqSchema } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { useSiteContent } from "../lib/useSiteContent";
import { FaqAccordion } from "../components/FaqAccordion";

const PATH = "/prices";

// ============================================================================
// DATA
// ============================================================================

const PACKAGES = [
  {
    name: "Studio Flat",
    icon: Sparkles,
    price: 99,
    description: "Compact studios up to ~35 sqm",
    duration: "3 – 4 hours",
    features: [
      "Full kitchen deep clean",
      "Bathroom limescale removal",
      "Bedroom area & wardrobes",
      "Floors, skirting & switches",
      "Internal windows & sills",
    ],
  },
  {
    name: "1 Bedroom",
    icon: Sparkles,
    price: 149,
    description: "Singles & couples — our most popular",
    duration: "4 – 5 hours",
    features: [
      "Everything in Studio",
      "Separate bedroom detailing",
      "Wardrobe interiors wiped",
      "All skirting hand-cleaned",
      "Light fittings & switches",
    ],
    popular: true,
  },
  {
    name: "2 Bedroom",
    icon: Sparkles,
    price: 179,
    description: "Sharers, couples and small families",
    duration: "5 – 7 hours",
    features: [
      "Everything in 1 Bedroom",
      "Second bedroom & wardrobe",
      "Additional bathroom",
      "Hallway & stair detailing",
      "All internal windows",
    ],
  },
  {
    name: "3 Bedroom",
    icon: Sparkles,
    price: 230,
    description: "Family homes & terraces",
    duration: "7 – 9 hours",
    features: [
      "Everything in 2 Bedroom",
      "Third bedroom & wardrobes",
      "Multiple bathrooms",
      "Stairs, landings & hallways",
      "Garden doors & approach",
    ],
  },
  {
    name: "4+ Bedroom",
    icon: Sparkles,
    price: 299,
    description: "Larger homes, HMOs & townhouses",
    duration: "9 – 12 hours",
    features: [
      "Everything in 3 Bedroom",
      "Additional bedrooms",
      "Multiple bathrooms & WCs",
      "Custom inventory items",
      "Two-cleaner team standard",
    ],
  },
];

const ADDONS = [
  {
    icon: Droplet,
    name: "Carpet Cleaning",
    price: "From £20 / room",
    description:
      "Hot-water extraction with pre-treatment for stains, pet odours and ground-in dirt. Fast 2–4 hour drying.",
    href: "/carpet-cleaning-london",
  },
  {
    icon: Flame,
    name: "Oven Cleaning",
    price: "From £20",
    description:
      "Dip-tank degreasing for single, double and range ovens. Hobs and extractors priced separately.",
    href: "/oven-cleaning-london",
  },
  {
    icon: Sofa,
    name: "Upholstery Cleaning",
    price: "From £30",
    description:
      "Steam extraction for fabric sofas, armchairs, dining chairs and mattresses. Stain treatment included.",
    href: "/contact",
  },
  {
    icon: TreePine,
    name: "Balcony Cleaning",
    price: "From £20",
    description:
      "Floor, railings, glass partitions and outdoor surfaces. Removes algae, soot and London weather buildup.",
    href: "/contact",
  },
  {
    icon: PaintRoller,
    name: "Wall Spot Cleaning",
    price: "£10 / spot",
    description:
      "Targeted scuff and mark removal on painted walls — perfect for clearing inventory wall-marking notes.",
    href: "/contact",
  },
];

const INCLUDED = [
  {
    title: "Kitchen Deep Clean",
    items: [
      "Worktops, sink and taps polished",
      "Inside & outside of cabinets",
      "Hob, splashback and tiles",
      "Fridge / freezer defrosted",
      "Floors and skirting",
    ],
  },
  {
    title: "Bathroom Sanitisation",
    items: [
      "Limescale stripped from tiles",
      "Toilet sanitised inside & out",
      "Shower screen and glass polished",
      "Grout & sealant scrubbed",
      "Mirrors and chrome polished",
    ],
  },
  {
    title: "Appliance Cleaning",
    items: [
      "Microwave inside & out",
      "Extractor filters degreased",
      "Washing machine drawer & seal",
      "Dishwasher filter cleared",
      "Kettle and toaster wiped",
    ],
  },
  {
    title: "Trim & Detail Work",
    items: [
      "Skirting boards hand-wiped",
      "Light switches & sockets",
      "Door handles & frames",
      "Tops of doors & wardrobes",
      "Picture rails & cornices",
    ],
  },
  {
    title: "Windows & Glass",
    items: [
      "All internal window glass",
      "Frames, sills and tracks",
      "Mirrors throughout the home",
      "Glass doors & panels",
      "Splashbacks polished",
    ],
  },
  {
    title: "Floors & Air",
    items: [
      "HEPA vacuuming throughout",
      "Hard floors mopped & detailed",
      "Carpet edges & corners",
      "Radiators dusted & wiped",
      "Final scented finish",
    ],
  },
];

const getWhyChoose = (sc) => [
  { icon: ShieldCheck,  title: sc.why1_title || "£5M insured cleaners",    description: sc.why1_desc || "Public liability cover on every visit — peace of mind for landlords and tenants alike." },
  { icon: CalendarCheck,title: sc.why2_title || "Flexible 7-day booking",  description: sc.why2_desc || "Online booking with live availability — same-day slots subject to capacity at month-end." },
  { icon: Clock,        title: sc.why3_title || "Weekend appointments",     description: sc.why3_desc || "Saturday and Sunday slots at no premium. Bank holidays bookable on request." },
  { icon: Leaf,         title: sc.why4_title || "Eco-friendly products",    description: sc.why4_desc || "Plant-based, fragrance-free chemistry as standard. Safe for kids, pets and sensitive landlords." },
  { icon: Users,        title: sc.why5_title || "Estate-agent standards",   description: sc.why5_desc || "Cleaning specified against the AIIC inventory benchmark used by major London letting agents." },
  { icon: Award,        title: sc.why6_title || "Re-clean focused",         description: sc.why6_desc || "7-day re-clean guarantee if your landlord flags any cleaning issue on the checkout report." },
];

const DEFAULT_FAQS = [
  {
    question: "How much does end of tenancy cleaning cost in London?",
    answer:
      "End of tenancy cleaning prices in London start from £99 for a studio flat, £149 for a one-bedroom, £179 for a two-bedroom, £230 for a three-bedroom and £299 for four-bedroom homes and larger. The final price depends on property size, condition and any add-ons such as carpet or oven cleaning. We send a fixed all-in quote up front — never an estimate that grows on the day.",
  },
  {
    question: "Is carpet cleaning included in the end of tenancy clean?",
    answer:
      "Carpet steam cleaning is priced separately as an add-on (from £25 per room) because not every tenancy requires it. If your tenancy agreement specifies professionally cleaned carpets, or your carpets are visibly soiled, we strongly recommend adding it — vacuuming alone does not satisfy a checkout report. Bundling carpet cleaning with the end of tenancy package unlocks an automatic discount.",
  },
  {
    question: "Do you guarantee deposit return?",
    answer:
      "No professional cleaner can sign your landlord's deposit decision for them — but we guarantee the cleaning standard. Every end of tenancy job comes with a written 7-day re-clean guarantee: if your landlord or inventory clerk raises any cleaning issue on the checkout report within 7 days, our team returns and re-cleans the flagged areas free of charge. That guarantee is the single biggest reason tenants clear their checkout report on the first attempt.",
  },
  {
    question: "How long does end of tenancy cleaning take in London?",
    answer:
      "A studio takes around 3 to 4 hours, a one-bedroom flat 4 to 5 hours, a two-bedroom property 5 to 7 hours, a three-bedroom home 7 to 9 hours, and four-bedroom houses 9 to 12 hours. Larger homes or heavily soiled properties are booked with a two-cleaner team as standard, so the job still finishes inside a single day.",
  },
  {
    question: "Are cleaning supplies and equipment included?",
    answer:
      "Yes — every quote is fully inclusive. Our cleaners arrive with professional-grade equipment (HEPA vacuums, dip-tanks for oven cleaning, hot-water carpet extractors), all the chemistry needed for the job and microfibre kits. You don't need to provide anything beyond water and electricity.",
  },
  {
    question: "Are there any hidden fees?",
    answer:
      "No. The price you see on our quote is the price you pay. No call-out fees, no on-the-day surprise charges, no congestion-charge add-ons, no parking surcharges unless explicitly flagged at quote time. The only adjustment we'd ever make is if the property turns out to be materially different from how it was described when we priced it — and we'd tell you before any change.",
  },
  {
    question: "Is your end of tenancy cleaning estate-agent approved?",
    answer:
      "Yes. Our cleaning specification mirrors the AIIC inventory benchmark used by the major London letting agents — Foxtons, Hamptons, Knight Frank, Dexters, Chestertons and most independents. We routinely sit on agents' preferred-supplier lists and our invoices are formatted for direct submission to a checkout report.",
  },
];

// ============================================================================
// PAGE
// ============================================================================

const Prices = () => {
  const sc = useSiteContent("prices", {
    eyebrow:    "Transparent Pricing",
    h1:         "Transparent End of Tenancy Cleaning Prices",
    highlight:  "in London",
    subheading: "Fixed quotes — no hidden fees, no surprise add-ons. Landlord and estate-agent approved cleaning backed by our 7-day re-clean guarantee.",
    studio:    99, oneBed: 149, twoBed: 179, threeBed: 230, fourBed: 299,
    pkgs_eyebrow: "Cleaning Packages", pkgs_heading: "Pick the package that fits your London property",
    pkgs_sub: "Every package is inventory-grade by default — the only difference is property size and the time on site. Add carpet cleaning, oven cleaning or upholstery work below to build the right bundle for your checkout.",
    inc_eyebrow: "What's included", inc_heading: "Every package covers the full inventory checklist",
    inc_sub: "Six zones, every surface — the same specification a London inventory clerk inspects against during a checkout report.",
    addons_eyebrow: "Optional Add-ons", addons_heading: "Pay only for the extras you actually need",
    addons_sub: "Bundle two or more add-ons with an end of tenancy package and your quote includes an automatic discount on each. Mix and match — every extra is priced clearly below.",
    why_eyebrow: "Why London chooses us", why_heading: "Premium cleaning, priced fairly",
    why_sub: "The reasons clients re-book Prime Tenancy and recommend us to their letting agents.",
    why1_title: "£5M insured cleaners",    why1_desc: "Public liability cover on every visit — peace of mind for landlords and tenants alike.",
    why2_title: "Flexible 7-day booking",  why2_desc: "Online booking with live availability — same-day slots subject to capacity at month-end.",
    why3_title: "Weekend appointments",    why3_desc: "Saturday and Sunday slots at no premium. Bank holidays bookable on request.",
    why4_title: "Eco-friendly products",   why4_desc: "Plant-based, fragrance-free chemistry as standard. Safe for kids, pets and sensitive landlords.",
    why5_title: "Estate-agent standards",  why5_desc: "Cleaning specified against the AIIC inventory benchmark used by major London letting agents.",
    why6_title: "Re-clean focused",        why6_desc: "7-day re-clean guarantee if your landlord flags any cleaning issue on the checkout report.",
    faq_heading: "End of tenancy cleaning prices — your questions answered",
    faq_sub: "The most asked pricing questions from London tenants, landlords and letting agents.",
    faq1_q: DEFAULT_FAQS[0].question, faq1_a: DEFAULT_FAQS[0].answer,
    faq2_q: DEFAULT_FAQS[1].question, faq2_a: DEFAULT_FAQS[1].answer,
    faq3_q: DEFAULT_FAQS[2].question, faq3_a: DEFAULT_FAQS[2].answer,
    faq4_q: DEFAULT_FAQS[3].question, faq4_a: DEFAULT_FAQS[3].answer,
    faq5_q: DEFAULT_FAQS[4].question, faq5_a: DEFAULT_FAQS[4].answer,
    faq6_q: DEFAULT_FAQS[5].question, faq6_a: DEFAULT_FAQS[5].answer,
    faq7_q: DEFAULT_FAQS[6].question, faq7_a: DEFAULT_FAQS[6].answer,
  });
  const WHY_CHOOSE = getWhyChoose(sc);
  const FAQS = [
    { question: sc.faq1_q, answer: sc.faq1_a }, { question: sc.faq2_q, answer: sc.faq2_a },
    { question: sc.faq3_q, answer: sc.faq3_a }, { question: sc.faq4_q, answer: sc.faq4_a },
    { question: sc.faq5_q, answer: sc.faq5_a }, { question: sc.faq6_q, answer: sc.faq6_a },
    { question: sc.faq7_q, answer: sc.faq7_a },
  ];
  return (
    <>
      <Seo
        title="Transparent End of Tenancy Cleaning Prices in London"
        description="End of tenancy cleaning prices London from £99. Fixed quotes, no hidden fees, landlord-approved cleaners, 7-day re-clean guarantee. See Studio – 4+ Bedroom pricing & add-ons."
        ogTitle="End of Tenancy Cleaning Prices London | Prime Tenancy"
        ogDescription="Transparent move out cleaning prices London — from £99. Landlord approved, re-clean guarantee, weekend slots."
        path={PATH}
        schema={[
          breadcrumbSchema([{ name: "Prices", path: PATH }]),
          faqSchema(FAQS),
          {
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            name: "End of Tenancy Cleaning Prices London",
            itemListElement: PACKAGES.map((p, i) => ({
              "@type": "Offer",
              position: i + 1,
              name: `${p.name} End of Tenancy Cleaning`,
              priceCurrency: "GBP",
              price: p.price,
              priceSpecification: {
                "@type": "PriceSpecification",
                price: p.price,
                priceCurrency: "GBP",
                valueAddedTaxIncluded: true,
              },
              areaServed: "London",
            })),
          },
        ]}
      />

      <PageHero
        eyebrow={sc.eyebrow}
        h1={sc.h1}
        highlight={sc.highlight}
        subheading={sc.subheading}
        breadcrumbs={[{ name: "Prices", path: PATH }]}
        image="https://images.unsplash.com/photo-1582731595174-9d18d4ed4c46?w=1920&q=80"
      />

      <PricingTrustStrip />
      <PricingCards sc={sc} />
      <Includes sc={sc} />
      <Addons sc={sc} />
      <WhyChooseUsBand sc={sc} items={WHY_CHOOSE} />
      <FaqAccordion heading={sc.faq_heading} subheading={sc.faq_sub} faqs={FAQS} background="slate" />
      <FinalCTA />
      <StickyMobileCTA />
    </>
  );
};

// ============================================================================
// SECTIONS
// ============================================================================

const PricingTrustStrip = () => (
  <section className="border-b border-slate-100 bg-white">
    <div className="container mx-auto px-6 md:px-12 lg:px-20">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-8">
        {[
          { icon: ShieldCheck, label: "No hidden fees" },
          { icon: Award, label: "Landlord approved" },
          { icon: HeartHandshake, label: "Re-clean guarantee" },
          { icon: CalendarCheck, label: "Same-day slots" },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.label}
              className="flex items-center gap-3 justify-center md:justify-start"
            >
              <div className="w-10 h-10 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-teal" />
              </div>
              <span className="text-navy font-semibold text-sm">
                {item.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const PricingCards = ({ sc }) => (
  <section
    id="packages"
    className="py-20 md:py-28 bg-white"
    data-testid="pricing-packages-section"
  >
    <div className="container mx-auto px-6 md:px-12 lg:px-20">
      <div className="max-w-3xl mb-14">
        <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.pkgs_eyebrow}</span>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold text-navy mt-3 mb-4 leading-tight">{sc.pkgs_heading}</h2>
        <p className="text-slate-600 text-lg leading-relaxed">{sc.pkgs_sub}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
        {PACKAGES.map((pkg, i) => {
          const prices = [sc.studio, sc.oneBed, sc.twoBed, sc.threeBed, sc.fourBed];
          return <PricingCard key={pkg.name} pkg={{ ...pkg, price: Number(prices[i]) || pkg.price }} index={i} />;
        })}
      </div>

      <p className="text-center text-slate-500 mt-10 text-sm max-w-2xl mx-auto">
        Prices shown are starting points. Final quote depends on property
        condition, access, and any add-ons. We always confirm in writing
        before you book — no surprise charges on the day.
      </p>
    </div>
  </section>
);

const PricingCard = ({ pkg, index }) => {
  const Icon = pkg.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.06 }}
      className={`relative bg-white rounded-2xl p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 ${
        pkg.popular
          ? "ring-2 ring-gold shadow-[0_10px_40px_-10px_rgba(212,168,83,0.45)]"
          : "border border-slate-100 shadow-[0_8px_30px_-12px_rgba(15,26,46,0.08)] hover:shadow-[0_14px_36px_-12px_rgba(15,26,46,0.18)] hover:border-teal/30"
      }`}
      data-testid={`price-package-${index}`}
    >
      {pkg.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy px-3.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-md">
          <Star className="w-3 h-3 fill-navy" />
          Most Popular
        </div>
      )}

      <div className="flex items-center gap-3 mb-5">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            pkg.popular ? "bg-gold/15" : "bg-teal/10"
          }`}
        >
          <Icon className={`w-5 h-5 ${pkg.popular ? "text-gold-dark" : "text-teal"}`} />
        </div>
        <div>
          <h3 className="font-heading text-lg font-semibold text-navy leading-tight">
            {pkg.name}
          </h3>
        </div>
      </div>

      <p className="text-slate-500 text-xs leading-relaxed mb-5 min-h-[2.5rem]">
        {pkg.description}
      </p>

      <div className="mb-5">
        <div className="flex items-baseline gap-1.5">
          <span className="text-slate-400 text-sm font-medium">from</span>
          <span className="font-heading text-4xl font-bold text-navy">
            £{pkg.price}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-slate-500 text-xs">
          <Clock className="w-3.5 h-3.5" />
          <span>{pkg.duration}</span>
        </div>
      </div>

      <div className="h-px bg-slate-100 mb-5" />

      <ul className="space-y-2.5 mb-7">
        {pkg.features.map((f) => (
          <li
            key={f}
            className="flex items-start gap-2 text-sm text-slate-600 leading-relaxed"
          >
            <CheckCircle className="w-4 h-4 text-teal flex-shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Link to="/get-a-quote" className="block">
        <Button
          className={`w-full font-semibold ${
            pkg.popular
              ? "bg-gold hover:bg-gold-dark text-navy"
              : "bg-navy hover:bg-navy-light text-white"
          }`}
          data-testid={`price-book-btn-${index}`}
        >
          Get a Quote
        </Button>
      </Link>
    </motion.div>
  );
};

const Includes = ({ sc }) => (
  <section className="py-20 md:py-28 bg-slate-50" data-testid="includes-section">
    <div className="container mx-auto px-6 md:px-12 lg:px-20">
      <div className="max-w-3xl mb-14">
        <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.inc_eyebrow}</span>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold text-navy mt-3 mb-4 leading-tight">{sc.inc_heading}</h2>
        <p className="text-slate-600 text-lg leading-relaxed">{sc.inc_sub}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {INCLUDED.map((group, i) => (
          <motion.div
            key={group.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-teal/30 transition-colors"
            data-testid={`include-group-${i}`}
          >
            <h3 className="font-heading text-base font-semibold text-navy mb-4 flex items-center gap-2">
              <span className="w-6 h-6 rounded-md bg-gold/15 text-gold-dark text-xs font-bold flex items-center justify-center">
                {String(i + 1).padStart(2, "0")}
              </span>
              {group.title}
            </h3>
            <ul className="space-y-2">
              {group.items.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-sm text-slate-600 leading-relaxed"
                >
                  <CheckCircle className="w-3.5 h-3.5 text-teal flex-shrink-0 mt-1" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      <div className="mt-12 max-w-3xl bg-white rounded-2xl p-6 md:p-7 border border-slate-100 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center flex-shrink-0">
          <Award className="w-6 h-6 text-gold" />
        </div>
        <div className="flex-1">
          <p className="font-heading font-semibold text-navy text-base mb-1">
            Need the same standard mid-tenancy?
          </p>
          <p className="text-slate-600 text-sm leading-relaxed">
            Our{" "}
            <Link
              to="/deep-cleaning-london"
              className="text-teal font-medium hover:text-navy"
            >
              one off deep cleaning in London
            </Link>{" "}
            applies the same checklist for landlords and homeowners — not just
            for checkouts.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const Addons = ({ sc }) => (
  <section className="py-20 md:py-28 bg-white" data-testid="addons-section">
    <div className="container mx-auto px-6 md:px-12 lg:px-20">
      <div className="max-w-3xl mb-14">
        <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.addons_eyebrow}</span>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold text-navy mt-3 mb-4 leading-tight">{sc.addons_heading}</h2>
        <p className="text-slate-600 text-lg leading-relaxed">{sc.addons_sub}</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {ADDONS.map((addon, i) => {
          const Icon = addon.icon;
          return (
            <motion.div
              key={addon.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group bg-slate-50 rounded-2xl p-6 hover:bg-navy transition-colors duration-300"
              data-testid={`addon-card-${i}`}
            >
              <div className="flex items-start justify-between mb-5 gap-3">
                <div className="w-11 h-11 rounded-xl bg-white group-hover:bg-white/10 flex items-center justify-center transition-colors">
                  <Icon className="w-5 h-5 text-teal" />
                </div>
                <span className="font-heading text-gold-dark group-hover:text-gold font-bold text-sm whitespace-nowrap transition-colors">
                  {addon.price}
                </span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-navy group-hover:text-white mb-2 transition-colors">
                {addon.name}
              </h3>
              <p className="text-slate-600 group-hover:text-slate-300 text-sm leading-relaxed mb-5 transition-colors">
                {addon.description}
              </p>
              <Link
                to={addon.href}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal group-hover:text-gold transition-colors"
              >
                Learn more
                <ArrowRight className="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

const WhyChooseUsBand = ({ sc, items }) => (
  <section className="py-20 md:py-28 bg-navy" data-testid="why-choose-section">
    <div className="container mx-auto px-6 md:px-12 lg:px-20">
      <div className="max-w-2xl mb-14">
        <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.why_eyebrow}</span>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mt-3 mb-4 leading-tight">{sc.why_heading}</h2>
        <p className="text-slate-300 text-lg leading-relaxed">{sc.why_sub}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((item, i) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-teal/30 transition-colors"
              data-testid={`why-choose-card-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-teal/10 flex items-center justify-center mb-5">
                <Icon className="w-5 h-5 text-teal" />
              </div>
              <h3 className="font-heading text-base font-semibold text-white mb-2">
                {item.title}
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="py-20 md:py-28 bg-white" data-testid="final-cta-section">
    <div className="container mx-auto px-6 md:px-12 lg:px-20">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="max-w-5xl mx-auto rounded-3xl overflow-hidden relative"
      >
        <div className="absolute inset-0 bg-navy" />
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1920&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-navy/80" />

        <div className="relative z-10 p-8 md:p-14 lg:p-16">
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 bg-gold/15 text-gold px-3 py-1.5 rounded-full text-xs font-semibold mb-5">
                <Clock className="w-3.5 h-3.5" />
                Last-minute checkouts welcome
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-5 leading-tight">
                Get your instant quote &amp;{" "}
                <span className="text-gold">book your London clean</span>
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-7 max-w-xl">
                Live booking takes under 60 seconds. Or call us — most quotes
                are back to you within a couple of working hours, and same-day
                slots are usually available at month-end.
              </p>

              <div className="flex flex-wrap gap-3">
                <Link to="/get-a-quote">
                  <Button
                    size="lg"
                    className="bg-gold hover:bg-gold-dark text-navy font-bold px-7 py-6 rounded-xl"
                    data-testid="final-book-btn"
                  >
                    Get Instant Quote
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:+442080363586">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 font-semibold px-7 py-6 rounded-xl"
                    data-testid="final-call-btn"
                  >
                    <Phone className="mr-2 w-4 h-4" />
                    020 8036 3586
                  </Button>
                </a>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <p className="text-xs font-semibold text-gold uppercase tracking-wider mb-4">
                  Browse related
                </p>
                <ul className="space-y-3">
                  {[
                    {
                      label: "End of Tenancy Cleaning London",
                      to: "/end-of-tenancy-cleaning-london",
                    },
                    {
                      label: "Deep Cleaning London",
                      to: "/deep-cleaning-london",
                    },
                    { label: "Contact us", to: "/contact" },
                    { label: "Full FAQ", to: "/faq" },
                  ].map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="flex items-center justify-between text-white hover:text-gold transition-colors text-sm group"
                      >
                        <span>{link.label}</span>
                        <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const StickyMobileCTA = () => (
  <div
    className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-8px_24px_-12px_rgba(15,26,46,0.18)]"
    data-testid="sticky-mobile-cta"
  >
    <div className="flex items-center gap-2 px-3 py-2.5 pr-24">
      <a
        href="tel:+442080363586"
        className="flex-1 flex items-center justify-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-navy font-semibold px-3 py-3 rounded-xl text-sm transition-colors"
        data-testid="sticky-call-btn"
      >
        <Phone className="w-4 h-4" />
        Call
      </a>
      <Link
        to="/get-a-quote"
        className="flex-1 flex items-center justify-center gap-1.5 bg-gold hover:bg-gold-dark text-navy font-bold px-3 py-3 rounded-xl text-sm transition-colors"
        data-testid="sticky-book-btn"
      >
        Get Instant Quote
        <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  </div>
);

export default Prices;
